import { useMarketDataSnapshot } from '@src/hooks';
import { usePrimeFromOrderBook } from '@src/hooks/usePrimeFromOrderBook';
import { useDeepDiveContext } from '@src/providers/DeepDiveProvider';
import {
  EMPTY_ARRAY,
  Stack,
  VolumeLadderContent,
  useElementSize,
  useHistoricalPrices,
  type OrderIntent,
  type SideEnum,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';

export function MobileVolumeLadderModule() {
  const {
    security,
    selectedMarkets,
    selectedMarketAccounts,
    selectedAggregation,
    selectedExchangeMarkets,
    securityExchangeMarkets,
    selectedCombined,
  } = useDeepDiveContext();
  const { elementRef, size } = useElementSize<HTMLDivElement>();
  const symbol = security?.Symbol;

  const sizeBuckets = security?.SizeBuckets;

  const requestedMarkets = useMemo(() => {
    if (selectedAggregation) {
      return selectedMarkets;
    }
    return selectedExchangeMarkets.length > 0 ? selectedExchangeMarkets : securityExchangeMarkets;
  }, [selectedAggregation, selectedMarkets, selectedExchangeMarkets, securityExchangeMarkets]);

  const requestedMarketAccounts = useMemo(() => {
    if (selectedAggregation) {
      return selectedMarketAccounts;
    }
    return selectedCombined.length > 0 ? selectedMarketAccounts : EMPTY_ARRAY;
  }, [selectedAggregation, selectedMarketAccounts, selectedCombined]);

  const { marketDataSnapshots: marketDataObservable } = useMarketDataSnapshot(
    symbol
      ? {
          symbol,
          sizeBuckets,
          marketAccounts: requestedMarketAccounts,
          markets: requestedMarkets,
          currency: security?.BaseCurrency,
          priceIncrement: security?.MinPriceIncrement,
          tag: 'MOBILE_SYMBOL_DEEP_DIVE_VOLUME_LADDER',
          includeTradeDirection: false,
        }
      : null
  );

  const { marketDataSnapshots: marketViewObservable } = useMarketDataSnapshot(
    symbol
      ? {
          symbol,
          marketAccounts: requestedMarketAccounts,
          markets: requestedMarkets,
          currency: security?.BaseCurrency,
          priceIncrement: security?.MinPriceIncrement,
          depth: 1,
          tag: 'MOBILE_SYMBOL_DEEP_DIVE_VOLUME_LADDER_TOB',
          includeTradeDirection: false,
        }
      : null
  );

  const sparklineDataObservable = useHistoricalPrices({
    symbol,
    resolution: '1M',
  });

  const primeFromOrderBook = usePrimeFromOrderBook({
    security,
    marketAccounts: selectedMarketAccounts,
    aggregation: selectedAggregation,
  });

  const handleClickRow = useCallback(
    (row: { side: SideEnum; price: string; size?: string; intent?: OrderIntent }) => {
      primeFromOrderBook({
        side: row.side,
        price: row.price,
        intent: row.intent,
        quantity: row.size,
      });
    },
    [primeFromOrderBook]
  );

  if (!security || marketDataObservable == null || marketViewObservable == null) {
    return null;
  }

  return (
    <Stack ref={elementRef} w="100%" h="100%">
      <VolumeLadderContent
        resolution="1M"
        onClickRow={handleClickRow}
        security={security}
        marketViewObservable={marketViewObservable}
        sparklineDataObservable={sparklineDataObservable}
        marketDataObservable={marketDataObservable}
        showSpread
        sizeBuckets={sizeBuckets ?? EMPTY_ARRAY}
        size={size}
        usePreciseTopOfBookIncrement={false}
      />
    </Stack>
  );
}
